import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import Loading from 'components/Loading';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Button,
  IconButton,
  Stack,
  Chip,
  Avatar,
  Paper,
  alpha,
  useTheme,
  Switch, // Add Switch
  FormControlLabel, // Add FormControlLabel
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import {
  Edit as ModeEditIcon,
  Business,
  AssignmentInd,
  ReceiptLong,
  Link as LinkIcon,
  LocationOn,
  Phone,
  Email,
  Settings, // Add Settings Icon
  AttachMoney,
  History,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import * as actions from '../../redux/actions';
import UpdateLabourTimestamp from '../../components/Forms/UpdateLabourTimestamp';
import api from '../../utils/functions/api';

const ACTION_COLOR = {
  CREATE: 'success',
  UPDATE: 'warning',
  DELETE: 'error',
};

export default function BusinessProfile({ title, subtitle }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();

  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const info_id = info?._id;

  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const [form, setForm] = useState({
    userId: '',
    salary_month: '',
    salary_day: '',
    salary_hour: '',
  });
  const [extraForm, setExtraForm] = useState({
    month: '',
    day: '',
  });
  const [allEmployees, setAllEmployees] = useState([]);

  const [activityRows, setActivityRows] = useState([]);
  const [activityCount, setActivityCount] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityModule, setActivityModule] = useState('');
  const [activityPage, setActivityPage] = useState(1);
  const activityPageSize = 50;
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [activityError, setActivityError] = useState('');

  useEffect(() => {
    dispatch(actions.meGet());
    dispatch(actions.getInformation());
  }, [dispatch]);

  useEffect(() => {
    const fetchAllEmployees = async () => {
      const PAGE_SIZE = 50;
      let pg = 1;
      let accumulated = [];
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get(
          `${apiBase}/employee?name=&size=${PAGE_SIZE}&page=${pg}&activeOnly=false`,
        );
        const rows = data?.rows || [];
        const total = data?.total || 0;
        accumulated = [...accumulated, ...rows];
        setAllEmployees([...accumulated]);
        hasMore = accumulated.length < total && rows.length > 0;
        pg++;
      }
    };
    fetchAllEmployees();
  }, [apiBase]);

  const fetchActivityLog = useCallback(async () => {
    setActivityLoading(true);
    setActivityError('');
    try {
      const qs = new URLSearchParams({
        page: String(activityPage),
        size: String(activityPageSize),
      });
      if (activityModule) qs.set('module', activityModule);
      const { data, status } = await api.get(
        `${apiBase}/activity-log?${qs.toString()}`,
      );
      if (status === 200 && data) {
        setActivityRows(data.rows || []);
        setActivityCount(data.count ?? 0);
      }
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        'โหลด log ไม่สำเร็จ';
      setActivityError(String(msg));
      setActivityRows([]);
      setActivityCount(0);
    } finally {
      setActivityLoading(false);
    }
  }, [activityModule, activityPage, apiBase]);

  useEffect(() => {
    fetchActivityLog();
  }, [fetchActivityLog]);

  const onSaveSalary = () => {
    const payload = {
      salary: {
        month: Number(form.salary_month),
        day: Number(form.salary_day),
        hr: Number(form.salary_day / 8),
      },
    };
    dispatch(actions.timestampPutSalary(form.userId, payload));
  };

  const onSaveExtraSalary = () => {
    const payload = {
      salary_extra: {
        month: Number(extraForm.month),
        day: Number(extraForm.day),
      },
    };
    dispatch(actions.timestampPutSalary(form.userId, payload));
  };

  const openDetail = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
  };

  const handleEdit = () => {
    history.push(`/management/business/edit/${info_id}`);
  };

  const BusinessInfoCard = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <Business />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              ข้อมูลบริษัท
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<ModeEditIcon />}
            onClick={handleEdit}
          >
            แก้ไขข้อมูล
          </Button>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  ข้อมูลพื้นฐาน
                </Typography>

                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ชื่อบริษัท
                    </Typography>
                    <Typography variant="body1">{info?.name || '-'}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      ชื่อภาษาอังกฤษ
                    </Typography>
                    <Typography variant="body1">
                      {info?.name_eng || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ReceiptLong fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        เลขที่ผู้เสียภาษี
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {info?.tax_id || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LinkIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        เว็บไซต์
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      component="a"
                      href={
                        info?.url?.startsWith('http')
                          ? info?.url
                          : `https://${info?.url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {info?.url || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  ข้อมูลติดต่อ
                </Typography>

                <Stack spacing={2} sx={{ mt: 2 }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        ที่อยู่
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {info?.address || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        เบอร์โทรศัพท์
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {info?.phone || '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Email fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        อีเมล
                      </Typography>
                    </Stack>
                    <Typography variant="body1">
                      {info?.email || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>
    );
  };

  const CompanySettingsCard = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
              <AssignmentInd />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              ข้อมูลเพิ่มเติม
            </Typography>
          </Stack>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    จำนวนพนักงาน
                  </Typography>
                  <Typography variant="body1">
                    {info?.employee_count || '-'} คน
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ประเภทธุรกิจ
                  </Typography>
                  <Typography variant="body1">
                    {info?.business_type || '-'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ปีที่ก่อตั้ง
                  </Typography>
                  <Typography variant="body1">
                    {info?.established_year || '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    สาขา
                  </Typography>
                  <Typography variant="body1">
                    {info?.branch_count || '-'} สาขา
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {info?.description && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    gutterBottom
                  >
                    รายละเอียดบริษัท
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {info?.description}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Paper>
    );
  };

  // New Card for Social Security Deduction
  const SocialSecurityDeductionCard = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.success.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: theme.palette.success.main }}>
              <AttachMoney />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              การหักประกันสังคม (SSO)
            </Typography>
          </Stack>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    อัตราการหักของพนักงาน
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {info?.setting?.payroll?.sso?.rate_percent || '-'} %
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ยอดเพดานสูงสุด
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {info?.setting?.payroll?.sso?.max_amount
                      ? `${info?.setting?.payroll?.sso?.max_amount.toLocaleString()} บาท`
                      : '-'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>
    );
  };

  // New Card for System Settings
  const SystemSettingsCard = () => {
    return (
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
              <Settings />
            </Avatar>
            <Typography variant="h6" fontWeight={600}>
              การตั้งค่าระบบ
            </Typography>
          </Stack>
        </Box>

        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  แผ่นงาน Google Sheet (Payout)
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {info?.google_sheet_worksheet || 'ใช้แผ่นแรก (ค่าเริ่มต้น)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ตั้งค่าที่ แก้ไขข้อมูลบริษัท → การตั้งค่า Google Sheet
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                รูปแบบลงเวลา (อ้างอิงโลเคชั่น หรือเลือกโครงการเอง) ตั้งค่าที่รายการพนักงาน → แก้ไขพนักงาน → รูปแบบลงเวลา
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>
    );
  };

  // No extra Paper/header wrapper here — UpdateLabourTimestamp already renders its own
  // Card with a "แก้ไขข้อมูลค่าแรง" header, wrapping it again would show that title twice.
  const SalaryRateCard = () => (
    <Box sx={{ mt: 3 }}>
      <UpdateLabourTimestamp
        employee={allEmployees}
        form={form}
        setForm={setForm}
        onSave={onSaveSalary}
        onSaveExtra={onSaveExtraSalary}
        setExtraForm={setExtraForm}
        extraForm={extraForm}
      />
    </Box>
  );

  const ActivityLogCard = () => (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <History />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              บันทึกกิจกรรม HR / Finance
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ย้อนหลังการสร้าง แก้ไข ลบรายการลงเวลาและค่าใช้จ่าย (รวม IP และ user จาก token เมื่อมี)
            </Typography>
          </Box>
        </Stack>
      </Box>
      <CardContent>
        {activityError ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {activityError}
            {apiBase ? '' : ' (ยังไม่ได้ตั้ง REACT_APP_API_URL)'}
          </Typography>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="โมดูล"
            size="small"
            value={activityModule}
            onChange={(e) => {
              setActivityPage(1);
              setActivityModule(e.target.value);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">ทั้งหมด</MenuItem>
            <MenuItem value="HR">HR</MenuItem>
            <MenuItem value="FINANCE">Finance</MenuItem>
          </TextField>
          <Button variant="outlined" onClick={fetchActivityLog} disabled={activityLoading}>
            รีเฟรช
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            ทั้งหมด {activityCount} รายการ
          </Typography>
        </Stack>

        {activityLoading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={32} />
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 420,
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>เวลา</TableCell>
                  <TableCell>โมดูล</TableCell>
                  <TableCell>ทรัพยากร</TableCell>
                  <TableCell>การกระทำ</TableCell>
                  <TableCell>ผู้ทำ (user)</TableCell>
                  <TableCell>สรุป</TableCell>
                  <TableCell align="right">รายละเอียด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">ยังไม่มี log</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  activityRows.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>
                        {row.createdAt
                          ? dayjs(row.createdAt).format('DD/MM/YYYY HH:mm:ss')
                          : '-'}
                      </TableCell>
                      <TableCell>{row.module}</TableCell>
                      <TableCell>{row.resourceType}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.action}
                          color={ACTION_COLOR[row.action] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {row.actorName || row.actorUsername || '-'}
                        {row.ip ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {row.ip}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" noWrap title={row.summary}>
                          {row.summary || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => openDetail(row)}>
                          JSON
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activityCount > activityPageSize ? (
          <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
            <Button
              size="small"
              disabled={activityPage <= 1}
              onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
            >
              ก่อนหน้า
            </Button>
            <Typography variant="body2" sx={{ alignSelf: 'center' }}>
              หน้า {activityPage}
            </Typography>
            <Button
              size="small"
              disabled={activityPage * activityPageSize >= activityCount}
              onClick={() => setActivityPage((p) => p + 1)}
            >
              ถัดไป
            </Button>
          </Stack>
        ) : null}
      </CardContent>
    </Paper>
  );

  if (!info?.isLoading && info?.isCompleted) {
    return (
      <Box sx={{ pb: 4 }}>
        <Box sx={{ mt: 3 }}>
          <BusinessInfoCard />
          <CompanySettingsCard />
          <SocialSecurityDeductionCard />
          <SystemSettingsCard /> {/* Add the new System Settings Card */}
          {/* Called as plain functions, not JSX tags (<SalaryRateCard />) — SalaryRateCard
              wraps UpdateLabourTimestamp, which has an internal effect that calls setForm
              on mount. Using JSX-tag syntax gives it a new component identity every render
              (redefined inline above), forcing React to unmount+remount it every render —
              mount → setForm → parent re-render → new identity → remount → infinite loop. */}
          {SalaryRateCard()}
          {ActivityLogCard()}
        </Box>

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>รายละเอียด log</DialogTitle>
          <DialogContent dividers>
            {detailRow ? (
              <Typography
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(
                  {
                    _id: detailRow._id,
                    module: detailRow.module,
                    resourceType: detailRow.resourceType,
                    action: detailRow.action,
                    resourceId: detailRow.resourceId,
                    summary: detailRow.summary,
                    actorUserId: detailRow.actorUserId,
                    actorName: detailRow.actorName,
                    actorUsername: detailRow.actorUsername,
                    ip: detailRow.ip,
                    userAgent: detailRow.userAgent,
                    before: detailRow.before,
                    after: detailRow.after,
                    meta: detailRow.meta,
                    createdAt: detailRow.createdAt,
                  },
                  null,
                  2,
                )}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>ปิด</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return <Loading isLoading />;
}
